'use client';

/**
 * GlobalErrorCapture
 *
 * 사용자 화면에서 발생하는 미처리 에러(window.onerror, unhandledrejection)를
 * trackAndLogError로 흘려 보낸다. AppErrorBoundary가 잡지 못하는 비동기 흐름,
 * 외부 라이브러리 에러까지 포괄.
 *
 * UI는 없다. (dashboard) 레이아웃의 ProtectedRoute 안쪽에서 한 번만 마운트.
 */

import { useEffect } from 'react';

import { trackAndLogError } from '@/lib/errors/errorTracker';

export function GlobalErrorCapture(): null {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      const err = e.error ?? new Error(e.message || 'window.onerror');
      trackAndLogError(
        err,
        {
          component: 'window.onerror',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          additionalData: {
            filename: e.filename,
            lineno: e.lineno,
            colno: e.colno,
          },
        },
        'error',
      );
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
      trackAndLogError(
        err,
        {
          component: 'unhandledrejection',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        'error',
      );
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
